/**
 * Creating a deal, and the reason a won or lost move asks for.
 *
 * A new deal can start from the deals page, from a company (the company is
 * filled in) or from a record on the leads page (its name is suggested and
 * the record's id is kept as `source_submission_id`, so the revenue that
 * record earned stays traceable to the deal).
 */
import { useEffect, useState } from 'react';
import {
  Alert, Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCreateDeal, useMoveDeal, usePipelines, type PipelineStage } from '../../api/crm';
import { useVocab } from '../../vocab';
import { CompanySelect, OwnerSelect, poundsToPence } from './crmShared';
import { RequiredPropertyInputs, requiredValues } from '../properties/PropertyFields';
import { useProperties } from '../../api/properties';

export type NewDealPrefill = {
  name?: string;
  companyId?: string | null;
  companyName?: string | null;
  pipelineId?: string | null;
  sourceSubmissionId?: string | null;
};

export function NewDealModal({
  opened, onClose, prefill, onCreated,
}: {
  opened: boolean;
  onClose: () => void;
  prefill?: NewDealPrefill;
  onCreated?: (id: string) => void;
}) {
  const v = useVocab();
  const pipelines = usePipelines(opened).data?.pipelines.filter((p) => !p.archived) ?? [];
  const create = useCreateDeal();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState<number | string>('');
  const [close, setClose] = useState('');
  const [owner, setOwner] = useState<string | null>(null);
  const [company, setCompany] = useState<{ id: string | null; name: string | null }>({ id: null, name: null });
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [props, setProps] = useState<Record<string, unknown>>({});
  const [error, setError] = useState('');
  const required = useProperties('deal', { enabled: opened }).data?.properties.filter((d) => d.required && !d.archived);

  // Fresh each time it opens, from whatever it was opened with.
  useEffect(() => {
    if (!opened) return;
    setName(prefill?.name ?? '');
    setAmount('');
    setClose('');
    setOwner(null);
    setCompany({ id: prefill?.companyId ?? null, name: prefill?.companyName ?? null });
    setPipelineId(prefill?.pipelineId ?? null);
    setProps({});
    setError('');
  }, [opened, prefill?.name, prefill?.companyId, prefill?.companyName, prefill?.pipelineId]);

  const chosenPipeline = pipelineId ?? pipelines[0]?.id ?? null;

  async function submit() {
    if (!name.trim()) {
      setError(`${v.A('deal')} needs a name.`);
      return;
    }
    const pence = poundsToPence(amount);
    if (amount !== '' && pence === null) {
      setError('The amount must be a number of pounds, from 0.');
      return;
    }
    setError('');
    const properties = requiredValues(required, props);
    try {
      const d = await create.mutateAsync({
        name: name.trim(),
        ...(properties ? { properties } : {}),
        ...(pence !== null ? { amount_pence: pence } : {}),
        ...(close ? { close_date: close } : {}),
        ...(owner ? { owner_user_id: owner } : {}),
        ...(company.id ? { company_id: company.id } : {}),
        ...(chosenPipeline ? { pipeline_id: chosenPipeline } : {}),
        ...(prefill?.sourceSubmissionId
          ? { source_submission_id: prefill.sourceSubmissionId, primary_person_ref: prefill.sourceSubmissionId }
          : {}),
      });
      notifications.show({ color: 'teal', title: `${v.One('deal')} created`, message: d.name });
      // With onCreated the caller decides where to go next, closing this
      // form as part of it; two separate updates would race each other.
      if (onCreated) onCreated(d.id);
      else onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title={`New ${v.one('deal')}`} size="md">
      <Stack>
        {error && <Alert color="red" variant="light">{error}</Alert>}
        <TextInput label="Name" required data-autofocus value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <Group grow align="flex-start" wrap="wrap">
          <NumberInput
            label="Amount"
            prefix="£"
            thousandSeparator=","
            decimalScale={2}
            min={0}
            allowNegative={false}
            value={amount}
            onChange={setAmount}
            miw={140}
          />
          <TextInput label="Close date" type="date" value={close} onChange={(e) => setClose(e.currentTarget.value)} miw={140} />
        </Group>
        <CompanySelect
          label={v.One('company')}
          value={company.id}
          valueLabel={company.name}
          onChange={(id, n) => setCompany({ id, name: n })}
        />
        <OwnerSelect label="Owner" value={owner} onChange={setOwner} />
        {pipelines.length > 1 && (
          <Select
            label={v.One('pipeline')}
            data={pipelines.map((p) => ({ value: p.id, label: p.name }))}
            value={chosenPipeline}
            onChange={setPipelineId}
            allowDeselect={false}
          />
        )}
        <RequiredPropertyInputs object="deal" values={props} onChange={setProps} />
        {prefill?.sourceSubmissionId && (
          <Text size="xs" c="dimmed">
            {`Linked to the ${v.one('lead')} it was created from, so its revenue stays traceable.`}
          </Text>
        )}
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} loading={create.isPending}>{`Create ${v.one('deal')}`}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Why a deal was won or lost, asked before the move is made. A lost deal
 * must say why; a won one may. Cancel leaves the deal where it was.
 */
export function StageReasonModal({
  stage, dealName, onCancel, onConfirm, busy,
}: {
  stage: PipelineStage | null;
  dealName: string;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  busy?: boolean;
}) {
  const v = useVocab();
  const [reason, setReason] = useState('');
  useEffect(() => setReason(''), [stage?.id]);
  const lost = stage?.kind === 'lost';
  return (
    <Modal
      opened={stage !== null}
      onClose={onCancel}
      title={lost ? `Mark ${dealName} as lost?` : `Mark ${dealName} as won?`}
      size="md"
      zIndex={1002}
    >
      <Stack>
        <Text size="sm">
          {lost
            ? `Moving it to ${stage?.name} marks the ${v.one('deal')} lost. Say why, so the reason stays with it.`
            : `Moving it to ${stage?.name} marks the ${v.one('deal')} won.`}
        </Text>
        <Textarea
          label={lost ? 'Why was it lost?' : 'Anything to note? (optional)'}
          required={lost}
          autosize
          minRows={2}
          data-autofocus
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onCancel}>Cancel</Button>
          <Button
            color={lost ? 'red' : undefined}
            disabled={lost && !reason.trim()}
            loading={busy}
            onClick={() => onConfirm(reason.trim())}
          >
            {lost ? 'Mark as lost' : 'Mark as won'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Moving a deal between stages, from the board, the table or the drawer.
 * An open stage moves at once; a won or lost stage asks for a reason first.
 * Returns `request` to start a move and the dialog to render.
 */
export function useStageMove() {
  const v = useVocab();
  const move = useMoveDeal();
  const [pending, setPending] = useState<{ deal: { id: string; name: string }; stage: PipelineStage } | null>(null);

  async function run(deal: { id: string; name: string }, stage: PipelineStage, reason?: string) {
    try {
      await move.mutateAsync({ id: deal.id, stageId: stage.id, reason });
      setPending(null);
    } catch (e) {
      notifications.show({ color: 'red', title: `Could not move the ${v.one('deal')}`, message: (e as Error).message });
    }
  }

  function request(deal: { id: string; name: string; stage_id: string }, stage: PipelineStage) {
    if (deal.stage_id === stage.id) return;
    if (stage.kind === 'open') void run(deal, stage);
    else setPending({ deal, stage });
  }

  const dialog = (
    <StageReasonModal
      stage={pending?.stage ?? null}
      dealName={pending?.deal.name ?? ''}
      busy={move.isPending}
      onCancel={() => setPending(null)}
      onConfirm={(reason) => {
        if (pending) void run(pending.deal, pending.stage, reason || undefined);
      }}
    />
  );
  return { request, dialog, moving: move.isPending ? (move.variables?.id ?? null) : null };
}
